import React, { FunctionComponent, useEffect, useState } from 'react';
import ModalWithHeader from '../../../../../../../common/components/presentation/ModalWithHeader';
import { DialogActions, DialogContent, Typography, Button, Box } from '@material-ui/core';
import accessoryPackModalStyles from './accessoryPackModalStyles';
import Price from '../../../../../../../gqlCommon/components/presentation/Price';
import useFeatureChildren from '../../../../../../hooks/queries/useFeatureChildren';
import AccessoriesList from '../../accessories/accessoriesList/AccessoriesList';
import { GetFeatureChildren_featureChildren } from '../../../../../../graphql/queries/__generated__/GetFeatureChildren';
import IPrice from '../../../../../../../gqlCommon/model/prices/IPrice';
import usePrice from '../../../../../../../gqlCommon/hooks/prices/usePrice';
import Translation from '../../../../../../../common/components/translation/Translation';
import { PriceDisplayType } from '../../../../../../../../../common/enum/PriceDisplayType';

export interface IAccessoryPackModalProps {
  featureCode: string;
  name: string;
  prices: IPrice[];
  isSelected: boolean;
  isOpen: boolean;
  onClose: () => void;
  onToggle?: (featureCode: string) => void;
}

const AccessoryPackModal: FunctionComponent<IAccessoryPackModalProps> = ({
  name,
  prices,
  isSelected,
  isOpen,
  onClose,
  onToggle,
  featureCode,
}) => {
  const styles = accessoryPackModalStyles();
  const [featureChildren, setFeatureChildren] = useState<GetFeatureChildren_featureChildren[]>([]);
  const { data, loading, loadFeatureChildren } = useFeatureChildren();
  const { primaryPrice, secondaryPrice } = usePrice(prices);

  useEffect(() => {
    if (isOpen) {
      loadFeatureChildren({
        featureCode,
      });
    }
  }, [isOpen, featureCode]);

  useEffect(() => {
    if (!loading && data) {
      setFeatureChildren(data?.featureChildren);
    }
  }, [data]);

  return (
    <ModalWithHeader title={name} open={isOpen} toggleModal={onClose} data-test={'container:accessory-pack'}>
      <DialogContent className={styles.modalContentContainer} data-test={'container:accessory-pack:accessories'}>
        <Box mb={2}>
          <Typography component="span" color={'textPrimary'} data-test={'text:accessory-pack:price'} variant="body1">
            <Translation id="trans__configurator__customise_page_options_section__features_modal_price_label" />
          </Typography>
          <Typography component="span" className={styles.price} variant="body1">
            <Price
              color="textPrimary"
              component="span"
              price={primaryPrice.money.amount}
              variant="body1"
              priceDisplayType={PriceDisplayType.PRIMARY_OPTIONS}
            />
          </Typography>
          {secondaryPrice && (
            <Typography
              component="span"
              className={styles.priceSecondary}
              data-test="text:accessory-pack:title:secondary"
              variant="body1"
            >
              <Typography component={'span'} color={'textPrimary'} variant="body1">
                <Translation
                  id={'trans__configurator__customise_page_options_section__features_modal_price_separator'}
                />
              </Typography>
              <Price
                data-test="price:accessory-pack:secondary"
                color="textPrimary"
                component="span"
                price={secondaryPrice.money.amount}
                variant="body1"
                priceDisplayType={PriceDisplayType.SECONDARY_OPTIONS}
              />
            </Typography>
          )}
        </Box>
        {<AccessoriesList accessories={featureChildren} inModal={true} isSelectable={false} />}
      </DialogContent>

      <DialogActions>
        <Button
          variant={isSelected ? 'outlined' : 'contained'}
          color={isSelected ? 'secondary' : 'primary'}
          onClick={() => onToggle(featureCode)}
          data-test={'button:accessory-pack:toggle'}
          size="small"
          className={`${styles.toggleButton} ${isSelected ? '' : styles.primaryButton}`}
        >
          <Translation
            id={
              isSelected
                ? 'trans__configurator__customise_page_options_section__features_modal_remove_cta'
                : 'trans__configurator__customise_page_options_section__features_modal_add_cta'
            }
          />
        </Button>
      </DialogActions>
    </ModalWithHeader>
  );
};

export default AccessoryPackModal;
